import { AlertNotification, Patient, PatientDetailedSensorData } from "@shared/models";
import { call, put, takeLatest } from "redux-saga/effects";
import {
  PaginatedResponse,
  ActionWithPayload,
  PatientFilter,
  UpdateStatusShape,
  AlertNotificationFilter,
  ImportResults,
  WithCallback,
  GetPatientDetailedSensorsDataFilter,
  GetPatientSensorsAverageDataFilter,
  PatientDetailedChartFilter,
  PatientDailyChartFilter,
} from "@shared/interfaces";
import { startLoading, stopLoading } from "@shared/store/actions";
import * as alertApi from "@containers/Alert/api";
import { PatientVitalSignsCalculation } from "@shared/models/PatientVitalSignsCalculation";

import api from "../api";
import {
  createPatient,
  getPatient,
  getPatients,
  updatePatient,
  updatePatientStatus,
  getPatientSensorsAverageData,
  getPatientDetailedSensorsData,
  getPatientDailyAlertNotifications,
  importPatients,
} from "./actions";
import { CreatePatientDto, CreatePatientSuccess, GetPatientSuccess, ImportPatientsDto } from "../interfaces";

function* getPatientsSaga({ payload }: ActionWithPayload<PatientFilter>) {
  try {
    yield put(startLoading());
    const response: PaginatedResponse<Patient> = yield call(api.getPatients, payload);
    yield put(stopLoading());
    yield put(
      getPatients.success({
        ...response,
        is_clear: !payload.page,
      }),
    );
  } catch (error) {
    yield put(getPatients.failure(error as Error));
    yield put(stopLoading());
  }
}

function* getPatientSaga({ payload }: ActionWithPayload<{ id: number }>) {
  try {
    yield put(startLoading());
    const response: GetPatientSuccess = yield call(api.getPatient, payload);
    yield put(stopLoading());
    yield put(getPatient.success(response));
  } catch (error) {
    yield put(getPatient.failure(error as Error));
    yield put(stopLoading());
  }
}

function* createPatientSaga({ payload }: ActionWithPayload<WithCallback<CreatePatientDto>>) {
  try {
    const { cb, ...rest } = payload;
    yield put(startLoading());
    const response: CreatePatientSuccess = yield call(api.createPatient, rest);
    yield put(stopLoading());
    yield put(createPatient.success(response));
    cb && cb();
  } catch (error) {
    yield put(createPatient.failure(error as Error));
    yield put(stopLoading());
  }
}

function* importPatientsSaga({ payload }: ActionWithPayload<ImportPatientsDto>) {
  try {
    yield put(startLoading());
    const response: ImportResults = yield call(api.importPatients, payload);
    yield put(stopLoading());
    yield put(importPatients.success(response));
  } catch (error) {
    yield put(importPatients.failure(error as Error));
    yield put(stopLoading());
  }
}

function* updatePatientSaga({ payload }: ActionWithPayload<WithCallback<CreatePatientDto & { id: number }>>) {
  try {
    const { cb, ...rest } = payload;
    yield put(startLoading());
    const response: CreatePatientSuccess = yield call(api.updatePatient, rest);
    yield put(stopLoading());
    yield put(updatePatient.success(response));
    cb && cb();
  } catch (error) {
    yield put(updatePatient.failure(error as Error));
    yield put(stopLoading());
  }
}

function* updatePatientStatusSaga({ payload }: ActionWithPayload<UpdateStatusShape>) {
  try {
    yield put(startLoading());
    const response: CreatePatientSuccess = yield call(api.updatePatientStatus, payload);
    yield put(stopLoading());
    yield put(updatePatientStatus.success(response));
  } catch (error) {
    yield put(updatePatientStatus.failure(error as Error));
    yield put(stopLoading());
  }
}

function* getPatientDetailedSensorsDataSaga({ payload }: ActionWithPayload<PatientDetailedChartFilter>) {
  try {
    yield put(startLoading());
    const data: PaginatedResponse<PatientDetailedSensorData> = yield call(
      api.getPatientDetailedSensorsData,
      Number(payload.patient_id),
      {
        date_from: payload.from,
        date_to: payload.to,
      } as GetPatientDetailedSensorsDataFilter,
    );
    yield put(getPatientDetailedSensorsData.success(data));
    yield put(stopLoading());
  } catch (e) {
    yield put(stopLoading());
    yield put(getPatientDetailedSensorsData.failure(e as Error));
  }
}

function* getPatientSensorsAverageDataSaga({ payload }: ActionWithPayload<PatientDailyChartFilter>) {
  try {
    yield put(startLoading());
    const data: PaginatedResponse<PatientVitalSignsCalculation> = yield call(
      api.getPatientSensorsAverageData,
      Number(payload.patient_id),
      {
        from: payload.from,
        to: payload.to,
        page: payload.page,
        limit: payload.limit,
      } as GetPatientSensorsAverageDataFilter,
    );
    yield put(getPatientSensorsAverageData.success(data));
    yield put(stopLoading());
  } catch (e) {
    yield put(stopLoading());
    yield put(getPatientSensorsAverageData.failure(e as Error));
  }
}

function* getPatientDailyAlertNotificationsSaga({ payload }: ActionWithPayload<AlertNotificationFilter>) {
  try {
    yield put(startLoading());
    const response: PaginatedResponse<AlertNotification> = yield call(alertApi.default.getAlertNotifications, payload);
    yield put(stopLoading());
    yield put(getPatientDailyAlertNotifications.success(response));
  } catch (error) {
    yield put(getPatientDailyAlertNotifications.failure(error as Error));
    yield put(stopLoading());
  }
}

function* patientSaga() {
  yield takeLatest(getPatients.request, getPatientsSaga);
  yield takeLatest(getPatient.request, getPatientSaga);
  yield takeLatest(createPatient.request, createPatientSaga);
  yield takeLatest(importPatients.request, importPatientsSaga);
  yield takeLatest(updatePatient.request, updatePatientSaga);
  yield takeLatest(updatePatientStatus.request, updatePatientStatusSaga);
  yield takeLatest(getPatientDetailedSensorsData.request, getPatientDetailedSensorsDataSaga);
  yield takeLatest(getPatientSensorsAverageData.request, getPatientSensorsAverageDataSaga);
  yield takeLatest(getPatientDailyAlertNotifications.request, getPatientDailyAlertNotificationsSaga);
}

export default patientSaga;
